import { Fragment } from 'react';
import type { Book } from '@booknest/shared';

import { BookCoverOnShelf } from './BookCoverOnShelf.js';
import { BookSpine } from './BookSpine.js';
import { ShelfDecoration } from './ShelfDecoration.js';
import { decorationSlots, type DecoSlot } from './spine-utils.js';

interface ShelfProps {
  label?: string;
  books: ReadonlyArray<Book>;
  /** Index de la rangée — détermine l'alternance des décos. */
  shelfIndex?: number;
  /** Mettre 1 livre toutes les N en cover face. 0 = jamais. */
  coverEvery?: number;
  /** Force le premier livre à être affiché face avant. */
  firstAsCover?: boolean;
  /** Si `true` : on ne montre pas le texte "vide" quand la rangée est sans
   *  livre. Sert pour les rangées vides "de remplissage" sous le contenu. */
  silentWhenEmpty?: boolean;
  openingBookId?: string | null;
  onBookClick?: (book: Book) => void;
}

export function Shelf({
  label,
  books,
  shelfIndex = 0,
  coverEvery = 0,
  firstAsCover = false,
  silentWhenEmpty = false,
  openingBookId = null,
  onBookClick,
}: ShelfProps): JSX.Element {
  const decos = decorationSlots(books.length, shelfIndex);
  const decoMap = new Map<number, DecoSlot>(decos.map((d) => [d.atIndex, d]));
  const endDeco = decoMap.get(books.length) ?? null;

  return (
    <div className="bookshelf">
      {label && <span className="shelf-label">{label}</span>}
      {books.length === 0 ? (
        silentWhenEmpty ? null : (
          <span className="px-4 pb-2 font-script text-sm italic text-cream-light/80">
            Cette étagère est vide…
          </span>
        )
      ) : (
        <>
          {books.map((book, idx) => {
            const deco = decoMap.get(idx);
            const isCover =
              (firstAsCover && idx === 0) ||
              (coverEvery > 0 && idx > 0 && idx % coverEvery === 0);
            return (
              <Fragment key={book.id}>
                {deco && <ShelfDecoration kind={deco.kind} size={deco.size} />}
                {isCover ? (
                  <BookCoverOnShelf
                    book={book}
                    opening={book.id === openingBookId}
                    onClick={onBookClick ? () => onBookClick(book) : undefined}
                  />
                ) : (
                  <BookSpine
                    book={book}
                    opening={book.id === openingBookId}
                    onClick={onBookClick ? () => onBookClick(book) : undefined}
                  />
                )}
              </Fragment>
            );
          })}
          {endDeco && (
            <ShelfDecoration kind={endDeco.kind} size={endDeco.size} />
          )}
        </>
      )}
    </div>
  );
}
